import {
  Condition,
  ConditionType,
  Deposit,
  DepositType,
  ValueType,
} from '@urbanx/agx.campaigns.client';
import { getNswDepositSplit } from '../contract-information/depositCalculations';

export type DepositPayableOption = 'quarter' | 'full';

interface ExchangeDepositState {
  salePrice?: number;
  deposit?: Deposit;
  depositPayableOption?: DepositPayableOption;
}

export const requiresSalePriceForSplitDollarDeposit = (state: ExchangeDepositState) =>
  state.deposit?.isDepositSelected === true &&
  state.deposit.depositValueType === ValueType.Dollar &&
  !!state.deposit.amount &&
  (state.depositPayableOption ?? 'quarter') === 'quarter' &&
  !state.salePrice;

export const buildExchangeDeposits = (state: ExchangeDepositState): Deposit[] | undefined => {
  const depositPercent =
    state.deposit?.depositValueType === ValueType.Percentage ? state.deposit.percent : undefined;
  const depositAmount =
    state.deposit?.depositValueType === ValueType.Dollar ? state.deposit.amount : undefined;
  const depositPayableOption = state.depositPayableOption ?? 'quarter';

  if (!state.deposit?.isDepositSelected || (!depositPercent && !depositAmount)) {
    return undefined;
  }

  const depositValueType = depositAmount ? ValueType.Dollar : ValueType.Percentage;

  if (depositPayableOption === 'full') {
    return [
      {
        depositType: DepositType.Initial,
        depositValueType,
        percent: depositPercent,
        amount: depositAmount,
        condition: Condition.OnDate,
        conditionType: ConditionType.Contract,
        isDepositSelected: true,
      },
    ];
  }

  const initialDepositPercent = 0.25;
  if (requiresSalePriceForSplitDollarDeposit(state)) {
    throw new Error('Sale price is required to save a split dollar deposit');
  }

  const balancePercent =
    depositPercent !== undefined ? depositPercent - initialDepositPercent : undefined;
  const { balanceDepositAmount } =
    depositAmount !== undefined && state.salePrice !== undefined
      ? getNswDepositSplit({
          totalDepositAmount: depositAmount,
          salePrice: state.salePrice,
          fullDepositUpfront: false,
        })
      : { balanceDepositAmount: undefined };

  return [
    {
      depositType: DepositType.Initial,
      depositValueType: ValueType.Percentage,
      percent: initialDepositPercent,
      amount: undefined,
      condition: Condition.OnDate,
      conditionType: ConditionType.Contract,
      isDepositSelected: true,
    },
    {
      depositType: DepositType.Balance,
      depositValueType,
      percent: balancePercent !== undefined && balancePercent > 0 ? balancePercent : undefined,
      amount: balanceDepositAmount,
      condition: Condition.OnDate,
      conditionType: ConditionType.Contract,
      isDepositSelected: true,
    },
  ].filter((deposit) => deposit.percent !== undefined || deposit.amount !== undefined);
};
